import type React from 'react';
import { forwardRef } from 'react';
import { Input, Button } from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import NumberFormatTyped from 'react-number-format';
import classnames from 'classnames';

import searchIcon from '@/shared/ui/icons/Components/search-icon.svg';
import clearIcon from '@/shared/ui/icons/Components/Common/close-icon.svg';

import SelectInput from './selectInput';
import useSearchInput from './useSearchInput';

// `keyDown` isn't a real NumberFormat prop (should be `onKeyDown`) —
// pre-existing typo, kept as-is.
const NumberFormat = NumberFormatTyped as any;

const components = {
  DropdownIndicator: null as any,
};

/**
 * Passes a numeric search input's text through unchanged. Acts as the identity formatter handed to NumberFormat so no mask is applied.
 *
 * @param value - Raw input text.
 * @returns The same text, unmodified.
 */
const format = (value: string) => value;

export type SearchInputHandle = {
  handleFocusInput: () => void;
};

export type SearchInputProps = {
  id?: string;
  className?: string;
  type?: string;
  name: string;
  placeholder: string;
  disabled?: boolean;
  value?: string;
  onChange: (name: string, value: string) => void;
  isSelectInput?: boolean;
  options?: any[];
  // only invoked when isMultiSearch is true (the plain-input path never calls it)
  onChangeMultiSearchValue?: (name: string, option: { label: string; value: string }) => void;
  isMultiSearch?: boolean;
  isNumberOnly?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  multiSearchValue?: any[];
  onBlur?: (e: React.FocusEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  shouldPreventFromInput?: boolean;
};

/**
 * Renders the shared search field in one of three shapes: a physician select, a multi-value creatable tag search, or a plain text or numeric input. Reads state and handlers from useSearchInput, exposes them through the forwarded ref, swaps the trailing icon between a clear button and a search glyph depending on whether a value is present, and sends numeric input through NumberFormat.
 *
 * @param props - Search input props such as id, type, name, placeholder, value, options, multiSearchValue and the change, key and focus callbacks.
 * @param ref - Forwarded handle exposing the imperative search input API.
 * @returns The rendered search field variant selected by isSelectInput and isMultiSearch.
 */
const SearchInput = forwardRef<SearchInputHandle, SearchInputProps>(
  (
    {
      id,
      className,
      type = 'text',
      name,
      placeholder,
      disabled = false,
      value = '',
      onChange,
      isSelectInput = false,
      options = [],
      onChangeMultiSearchValue,
      isMultiSearch = false,
      isNumberOnly = false,
      onKeyDown = () => {},
      multiSearchValue = [],
      onBlur = () => {},
      onFocus = () => {},
      shouldPreventFromInput = false,
    },
    ref,
  ) => {
    const {
      textInputRef,
      state,
      handleChange,
      onValueChange,
      onChangeSelection,
      onClickClearSearch,
      onChangeCreatableSelect,
      onInputChangeCreatableSelect,
      onKeyDownCreatableSelect,
    } = useSearchInput(
      {
        id,
        name,
        onChange,
        onChangeMultiSearchValue,
        isNumberOnly,
        multiSearchValue,
        shouldPreventFromInput,
      },
      ref,
    );

    return isSelectInput ? (
      <SelectInput
        id={id}
        className="w-100"
        name="physician"
        isSearchable
        isClearable
        placeholder="Search by Physician"
        disabled={disabled}
        // SearchInput's `value` is a string (per PropTypes) but SelectInput
        // expects an option object/array — pre-existing mismatch.
        value={value as any}
        options={options}
        onChange={onChangeSelection}
        isHideDefaultDropdownIndicator
        dropdownIndicatorComponent={
          !value && (
            <img
              className={classnames(
                'custom-search-input__clear-icon',
                disabled ? '--disabled' : '',
              )}
              src={searchIcon}
              alt="Search icon"
            />
          )
        }
      />
    ) : isMultiSearch ? (
      <div className={classnames('custom-search-input', className)}>
        <CreatableSelect
          id={id}
          components={components}
          inputValue={state.inputValue}
          isMulti
          menuIsOpen={false}
          onChange={onChangeCreatableSelect}
          onInputChange={onInputChangeCreatableSelect}
          onKeyDown={onKeyDownCreatableSelect}
          placeholder={placeholder}
          value={multiSearchValue}
        />
      </div>
    ) : (
      <div className={classnames('custom-search-input', className)}>
        {type === 'number' ? (
          <NumberFormat
            id={id}
            className="custom-search-input__input"
            placeholder={placeholder}
            name={name}
            onValueChange={onValueChange}
            value={value}
            disabled={disabled}
            allowNegative={false}
            isNumericString
            format={format}
            keyDown={onKeyDown}
          />
        ) : (
          <Input
            innerRef={textInputRef}
            id={id}
            className="custom-search-input__input"
            type={type as any}
            placeholder={placeholder}
            name={name}
            onChange={handleChange}
            value={value}
            disabled={disabled}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        )}

        {value ? (
          <Button className="custom-search-input__clear-btn" onClick={onClickClearSearch}>
            <img src={clearIcon} alt="Clear icon" />
          </Button>
        ) : (
          <img
            className={classnames('custom-search-input__clear-icon', disabled ? '--disabled' : '')}
            src={searchIcon}
            alt="Search icon"
          />
        )}
      </div>
    );
  },
);

export default SearchInput;
